import React, { useCallback, useMemo, useState } from 'react';
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  ActivityIndicator,
  StyleSheet,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useTheme } from '../../contexts/ThemeContext';
import { useCalendarGlassStyles } from './calendarGlassStyles';
import { useMasterTourCalendarUi } from '../../contexts/FeatureFlagsContext';
import { TourStopSocialFooter } from './TourStopSocialFooter';
import { useTourPartyMembers } from '../../hooks/useTourPartyMembers';
import {
  findTourDayByDate,
  getInterDayTravelSummary,
  getLodgingVenueForDay,
  getStopParticipants,
  normalizePinnedStops,
  venueHasLodgingFlag,
} from '../../utils/tourDayLodgingUtils';
import { stopKeyForPin, stopKeyForVenue, LODGING_ACCENT_COLOR, lodgingCardStyle } from '../../utils/tourLodgingUi';
import { palette } from '../../styles/tourTheme';
import { updateTourDayFields } from '../../utils/tourCalendarSyncService';

function StopCard({
  title,
  subtitle,
  badge,
  isLodging,
  isJoined,
  canEdit,
  onToggleLodging,
  onToggleJoin,
  participantIds,
  members,
  theme,
  isDarkMode,
  styles,
}) {
  return (
    <View style={[styles.stopCard, isLodging && styles.stopCardLodging]}>
      <View style={styles.stopCardMain}>
        <View style={styles.stopCardText}>
          {badge ? (
            <View style={styles.stopBadgeRow}>
              <Text style={styles.stopBadge}>{badge}</Text>
            </View>
          ) : null}
          {isLodging ? (
            <View style={styles.lodgingPillRow}>
              <Ionicons name="bed-outline" size={11} color={LODGING_ACCENT_COLOR} />
              <Text style={styles.lodgingPillText}>Tonight's lodging</Text>
            </View>
          ) : null}
          <Text style={styles.stopTitle} numberOfLines={2}>{title}</Text>
          {subtitle ? <Text style={styles.stopSubtitle} numberOfLines={2}>{subtitle}</Text> : null}
        </View>
        <TourStopSocialFooter
          theme={theme}
          isDarkMode={isDarkMode}
          isLodging={isLodging}
          isJoined={isJoined}
          canEditLodging={canEdit}
          onToggleLodging={onToggleLodging}
          onToggleJoin={onToggleJoin}
          participantIds={participantIds}
          members={members}
        />
      </View>
    </View>
  );
}

export function LodgingDayPanel({
  ownerId,
  tourId,
  tour,
  dateKey,
  currentUserId,
  canEdit = true,
  onTourUpdated,
  onOpenTourPlanner,
  onToast,
  theme,
  ownerName,
  ownerEmail,
  ownerPhotoURL,
}) {
  const { isDarkMode } = useTheme();
  const masterTourUi = useMasterTourCalendarUi();
  const styles = useMemo(() => createStyles(theme, isDarkMode, masterTourUi), [theme, isDarkMode, masterTourUi]);
  const glass = useCalendarGlassStyles(theme, isDarkMode);
  const [saving, setSaving] = useState(false);
  const [pinDraft, setPinDraft] = useState({ name: '', location: '', note: '' });
  const [showPinForm, setShowPinForm] = useState(false);
  const { members: tourPartyMembers } = useTourPartyMembers({
    ownerId,
    ownerName,
    ownerEmail,
    ownerPhotoURL,
    tourId,
  });

  const lodgingRows = useMemo(() => {
    const days = Array.isArray(tour?.days) ? tour.days : [];
    return days
      .map((d, index) => {
        const dk = String(d?.scheduledDate || '').trim();
        if (!dk) return null;
        const lodging = getLodgingVenueForDay(d);
        const first = (d?.venues || []).find((v) => String(v?.name || '').trim());
        const place = lodging || first;
        const city = [place?.city, place?.state].filter(Boolean).join(', ');
        return {
          index,
          dateKey: dk,
          dateLabel: dk.slice(5).replace('-', '/'),
          city: city || '—',
          hotel: String(lodging?.name || '').trim() || '—',
          addr: String(lodging?.address || lodging?.location || '').trim(),
          status: lodging ? 'Held' : 'Open',
        };
      })
      .filter(Boolean);
  }, [tour]);
  const { day, dayIndex } = useMemo(() => findTourDayByDate(tour, dateKey), [tour, dateKey]);
  const prevDay = dayIndex > 0 ? tour?.days?.[dayIndex - 1] : null;
  const travelSummary = useMemo(() => getInterDayTravelSummary(tour, dayIndex), [tour, dayIndex]);
  const lodgingVenue = useMemo(() => getLodgingVenueForDay(day), [day]);
  const pinnedStops = useMemo(() => normalizePinnedStops(day?.pinnedStops), [day?.pinnedStops]);
  const venues = day?.venues || [];

  const saveDayFields = useCallback(
    async (patch, successMessage) => {
      if (!ownerId || !tourId || dayIndex < 0) return;
      try {
        setSaving(true);
        await updateTourDayFields(ownerId, tourId, dayIndex, patch);
        onTourUpdated?.();
        if (successMessage) onToast?.(successMessage, 'success');
      } catch (err) {
        onToast?.(err?.message || 'Could not save changes.', 'error');
      } finally {
        setSaving(false);
      }
    },
    [ownerId, tourId, dayIndex, onTourUpdated, onToast]
  );

  const toggleLodgingVenue = useCallback(
    (venueId) => {
      if (!day || !canEdit) return;
      const nextId = day.lodgingVenueId === venueId ? null : venueId;
      saveDayFields(
        { lodgingVenueId: nextId },
        nextId ? 'Marked lodging for this day.' : 'Cleared lodging for this day.'
      );
    },
    [day, canEdit, saveDayFields]
  );

  const toggleParticipation = useCallback(
    (stopKey) => {
      if (!day || !currentUserId) return;
      const map = { ...(day.stopParticipation || {}) };
      const current = getStopParticipants(day, stopKey);
      const joined = current.includes(currentUserId);
      map[stopKey] = joined
        ? current.filter((id) => id !== currentUserId)
        : [...current, currentUserId];
      saveDayFields(
        { stopParticipation: map },
        joined ? 'Removed you from this stop.' : 'You joined this stop.'
      );
    },
    [day, currentUserId, saveDayFields]
  );

  const addPinnedStop = useCallback(() => {
    if (!canEdit) return;
    const name = pinDraft.name.trim();
    const location = pinDraft.location.trim();
    const note = pinDraft.note.trim();
    if (!name && !location) {
      onToast?.('Add a name or location for your pinned stop.', 'error');
      return;
    }
    const pinId = `pin-${Date.now().toString(36)}`;
    const stopKey = stopKeyForPin(pinId);
    const next = [
      ...pinnedStops,
      {
        id: pinId,
        name,
        location,
        note,
        participants: currentUserId ? [currentUserId] : [],
      },
    ];
    const map = { ...(day?.stopParticipation || {}) };
    if (currentUserId) map[stopKey] = [currentUserId];
    saveDayFields({ pinnedStops: next, stopParticipation: map }, 'Pinned stop added.');
    setPinDraft({ name: '', location: '', note: '' });
    setShowPinForm(false);
  }, [canEdit, pinDraft, pinnedStops, currentUserId, day?.stopParticipation, saveDayFields, onToast]);

  const removePinnedStop = useCallback(
    (pinId) => {
      if (!canEdit) return;
      const next = pinnedStops.filter((p) => p.id !== pinId);
      const map = { ...(day?.stopParticipation || {}) };
      delete map[stopKeyForPin(pinId)];
      saveDayFields({ pinnedStops: next, stopParticipation: map }, 'Pinned stop removed.');
    },
    [canEdit, pinnedStops, day?.stopParticipation, saveDayFields]
  );

  if (!dateKey || !tourId) {
    return (
      <View style={glass.cardInset}>
        <Text style={styles.emptyText}>Select a tour and day to view lodging.</Text>
      </View>
    );
  }

  if (!day) {
    return (
      <View style={glass.cardInset}>
        <Text style={styles.emptyText}>
          No Tour Planner day is linked to {dateKey}. Schedule this date in Tour Planner first.
        </Text>
        {onOpenTourPlanner ? (
          <TouchableOpacity style={styles.linkBtn} onPress={() => onOpenTourPlanner(tourId)}>
            <Ionicons name="map-outline" size={14} color={theme.brand.primary} />
            <Text style={styles.linkBtnText}>Open Tour Planner</Text>
          </TouchableOpacity>
        ) : null}
      </View>
    );
  }

  return (
    <View style={styles.wrap}>
      {masterTourUi && lodgingRows.length ? (
        <View style={styles.table}>
          <Text style={styles.tableTitle}>Lodging</Text>
          <View style={styles.tableHead}>
            <Text style={[styles.tableHeadCell, styles.colDate]}>Date</Text>
            <Text style={[styles.tableHeadCell, styles.colCity]}>City</Text>
            <Text style={[styles.tableHeadCell, styles.colHotel]}>Hotel</Text>
            <Text style={[styles.tableHeadCell, styles.colStatus]}>Status</Text>
          </View>
          {lodgingRows.map((row) => {
            const active = row.dateKey === dateKey;
            return (
              <View key={row.dateKey} style={[styles.tableRow, active && styles.tableRowActive]}>
                <Text style={[styles.tableCell, styles.colDate, active && styles.tableCellActive]}>{row.dateLabel}</Text>
                <Text style={[styles.tableCell, styles.colCity]} numberOfLines={1}>{row.city}</Text>
                <View style={styles.colHotel}>
                  <Text style={[styles.tableHotel, row.hotel === '—' && styles.tableMuted]} numberOfLines={1}>{row.hotel}</Text>
                  {row.addr ? <Text style={styles.tableAddr} numberOfLines={1}>{row.addr}</Text> : null}
                </View>
                <Text style={[styles.tableStatus, row.status === 'Held' ? styles.statusHeld : styles.statusOpen]}>
                  {row.status}
                </Text>
              </View>
            );
          })}
        </View>
      ) : null}

      {saving ? (
        <View style={styles.savingRow}>
          <ActivityIndicator size="small" color={theme.brand.accent} />
          <Text style={styles.savingText}>Saving…</Text>
        </View>
      ) : null}

      {prevDay?.busCall?.lat ? (
        <View style={glass.cardInset}>
          <Text style={styles.sectionLabel}>Bus call (from previous day)</Text>
          <Text style={styles.bodyStrong}>{prevDay.busCall.name || 'Bus call location'}</Text>
          {prevDay.busCall.time ? <Text style={styles.bodyMuted}>🕐 {prevDay.busCall.time}</Text> : null}
        </View>
      ) : null}

      {travelSummary ? (
        <View style={glass.cardInset}>
          <Text style={styles.sectionLabel}>Travel to today</Text>
          {travelSummary.same ? (
            <Text style={styles.bodyMuted}>{travelSummary.modeLabel}</Text>
          ) : (
            <>
              <Text style={styles.bodyStrong}>
                {travelSummary.fromLabel} → {travelSummary.toLabel}
              </Text>
              <Text style={styles.bodyMuted}>
                {travelSummary.miles} mi · {travelSummary.timeLabel} · {travelSummary.modeLabel}
              </Text>
            </>
          )}
        </View>
      ) : null}

      {lodgingVenue ? (
        <View style={[glass.cardInset, styles.lodgingHighlight]}>
          <Text style={styles.sectionLabel}>Tonight's lodging</Text>
          <Text style={styles.bodyStrong}>{lodgingVenue.name || 'Lodging stop'}</Text>
          {[lodgingVenue.city, lodgingVenue.state].filter(Boolean).length ? (
            <Text style={styles.bodyMuted}>
              {[lodgingVenue.city, lodgingVenue.state].filter(Boolean).join(', ')}
            </Text>
          ) : null}
        </View>
      ) : (
        <View style={glass.cardInset}>
          <Text style={styles.sectionLabel}>Tonight's lodging</Text>
          <Text style={styles.bodyMuted}>
            Mark a stop below as lodging, or add a hotel from Tour Planner to this day.
          </Text>
        </View>
      )}

      {day.busCall?.lat || day.busCall?.time ? (
        <View style={glass.cardInset}>
          <Text style={styles.sectionLabel}>Bus call (after show)</Text>
          {day.busCall.name ? <Text style={styles.bodyStrong}>{day.busCall.name}</Text> : null}
          {day.busCall.time ? <Text style={styles.bodyMuted}>🕐 {day.busCall.time}</Text> : null}
          {onOpenTourPlanner ? (
            <TouchableOpacity style={styles.linkBtn} onPress={() => onOpenTourPlanner(tourId)}>
              <Text style={styles.linkBtnText}>Edit in Tour Planner</Text>
            </TouchableOpacity>
          ) : null}
        </View>
      ) : null}

      {venues.length > 0 ? (
        <View style={styles.sectionBlock}>
          <Text style={styles.sectionLabel}>Day stops</Text>
          {venues.map((venue) => {
            const venueId = String(venue?.id || '');
            const isLodging = day.lodgingVenueId === venueId;
            const stopKey = stopKeyForVenue(venueId);
            const participantIds = getStopParticipants(day, stopKey);
            const isJoined = currentUserId ? participantIds.includes(currentUserId) : false;
            return (
              <StopCard
                key={venueId || venue.name}
                title={venue.name || 'Stop'}
                subtitle={[venue.city, venue.state].filter(Boolean).join(', ')}
                badge={venueHasLodgingFlag(venue) ? 'Hotel / lodging venue' : null}
                isLodging={isLodging}
                isJoined={isJoined}
                canEdit={canEdit}
                onToggleLodging={canEdit ? () => toggleLodgingVenue(venueId) : null}
                onToggleJoin={currentUserId ? () => toggleParticipation(stopKey) : null}
                participantIds={participantIds}
                members={tourPartyMembers}
                theme={theme}
                isDarkMode={isDarkMode}
                styles={styles}
              />
            );
          })}
        </View>
      ) : null}

      <View style={styles.sectionBlock}>
        <View style={styles.sectionHeadRow}>
          <Text style={styles.sectionLabel}>Pinned stops</Text>
          {canEdit ? (
            <TouchableOpacity style={styles.linkBtn} onPress={() => setShowPinForm((v) => !v)}>
              <Ionicons name={showPinForm ? 'remove-circle-outline' : 'add-circle-outline'} size={14} color={theme.brand.primary} />
              <Text style={styles.linkBtnText}>{showPinForm ? 'Cancel' : 'Pin a stop'}</Text>
            </TouchableOpacity>
          ) : null}
        </View>
        <Text style={styles.hintText}>
          Add personal plans — dinner, errands, meetups — with a name and location for this day.
        </Text>
        {showPinForm ? (
          <View style={styles.pinForm}>
            <TextInput
              style={styles.input}
              value={pinDraft.name}
              onChangeText={(t) => setPinDraft((p) => ({ ...p, name: t }))}
              placeholder="Stop name (e.g. Late dinner)"
              placeholderTextColor={theme.input.placeholder}
            />
            <TextInput
              style={styles.input}
              value={pinDraft.location}
              onChangeText={(t) => setPinDraft((p) => ({ ...p, location: t }))}
              placeholder="Location or address"
              placeholderTextColor={theme.input.placeholder}
            />
            <TextInput
              style={[styles.input, styles.inputMultiline]}
              value={pinDraft.note}
              onChangeText={(t) => setPinDraft((p) => ({ ...p, note: t }))}
              placeholder="Notes (optional)"
              placeholderTextColor={theme.input.placeholder}
              multiline
            />
            <TouchableOpacity style={styles.primaryBtn} onPress={addPinnedStop}>
              <Text style={styles.primaryBtnText}>Add pinned stop</Text>
            </TouchableOpacity>
          </View>
        ) : null}
        {pinnedStops.length === 0 && !showPinForm ? (
          <Text style={styles.bodyMuted}>No pinned stops yet.</Text>
        ) : (
          pinnedStops.map((pin) => {
            const stopKey = stopKeyForPin(pin.id);
            const participantIds = getStopParticipants(day, stopKey);
            const pinJoined = pin.participants.includes(currentUserId) || participantIds.includes(currentUserId);
            return (
              <View key={pin.id} style={styles.pinnedRow}>
                <StopCard
                  title={pin.name || 'Pinned stop'}
                  subtitle={[pin.location, pin.note].filter(Boolean).join(' · ')}
                  isJoined={currentUserId ? pinJoined : false}
                  canEdit={false}
                  onToggleJoin={currentUserId ? () => toggleParticipation(stopKey) : null}
                  participantIds={participantIds}
                  members={tourPartyMembers}
                  theme={theme}
                  isDarkMode={isDarkMode}
                  styles={styles}
                />
                {canEdit ? (
                  <TouchableOpacity style={styles.removePinBtn} onPress={() => removePinnedStop(pin.id)}>
                    <Text style={styles.removePinText}>Remove</Text>
                  </TouchableOpacity>
                ) : null}
              </View>
            );
          })
        )}
      </View>

      {onOpenTourPlanner ? (
        <TouchableOpacity style={styles.plannerBtn} onPress={() => onOpenTourPlanner(tourId)}>
          <Ionicons name="map-outline" size={16} color={theme.brand.primary} />
          <Text style={styles.plannerBtnText}>Open full route in Tour Planner</Text>
        </TouchableOpacity>
      ) : null}
    </View>
  );
}

function createStyles(theme, isDarkMode, masterTourUi = false) {
  const pal = palette(isDarkMode);
  const textPrimary = masterTourUi ? pal.ink : theme.text.primary;
  const textSecondary = masterTourUi ? pal.mute : theme.text.secondary;
  const textTertiary = masterTourUi ? pal.mute : theme.text.tertiary;
  const lodgingSurface = lodgingCardStyle(isDarkMode);
  return StyleSheet.create({
    wrap: { gap: 10 },
    table: {
      borderWidth: 1,
      borderColor: masterTourUi ? pal.line : '#e4e4df',
      borderRadius: 12,
      overflow: 'hidden',
      backgroundColor: masterTourUi ? pal.surf : '#fffffe',
      marginBottom: 6,
    },
    tableTitle: {
      fontSize: 18,
      fontWeight: '700',
      letterSpacing: -0.3,
      color: masterTourUi ? pal.ink : '#14141a',
      paddingHorizontal: 14,
      paddingTop: 12,
      paddingBottom: 8,
      borderBottomWidth: 2,
      borderBottomColor: pal.teal,
      fontFamily: "'Public Sans', system-ui, sans-serif",
    },
    tableHead: {
      flexDirection: 'row',
      gap: 8,
      paddingHorizontal: 14,
      paddingVertical: 8,
    },
    tableHeadCell: {
      fontSize: 10,
      fontWeight: '700',
      letterSpacing: 1,
      textTransform: 'uppercase',
      color: masterTourUi ? pal.mute : '#6a6a65',
    },
    tableRow: {
      flexDirection: 'row',
      alignItems: 'flex-start',
      gap: 8,
      paddingHorizontal: 14,
      paddingVertical: 10,
      borderTopWidth: 1,
      borderTopColor: masterTourUi ? pal.lineSoft : '#f0f0eb',
    },
    tableRowActive: { backgroundColor: masterTourUi ? pal.page : '#f4f4f2' },
    tableCell: { fontSize: 13, color: masterTourUi ? pal.ink : '#14141a' },
    tableCellActive: { fontWeight: '700' },
    tableHotel: { fontSize: 13, fontWeight: '500', color: '#2f6ed1' },
    tableMuted: { color: '#6a6a65', fontWeight: '400' },
    tableAddr: { fontSize: 11, color: '#6a6a65', marginTop: 2 },
    tableStatus: {
      fontSize: 11,
      fontWeight: '700',
      letterSpacing: 0.6,
      textTransform: 'uppercase',
      textAlign: 'center',
    },
    statusHeld: { color: '#2a7d5c' },
    statusOpen: { color: '#6a6a65' },
    colDate: { width: 52, flexGrow: 0, flexShrink: 0 },
    colCity: { flex: 1, minWidth: 0 },
    colHotel: { flex: 1.2, minWidth: 0 },
    colStatus: { width: 52, textAlign: 'center' },
    savingRow: { flexDirection: 'row', alignItems: 'center', gap: 8 },
    savingText: { fontSize: 12, color: textSecondary },
    sectionBlock: { gap: 8 },
    sectionHeadRow: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: 8 },
    sectionLabel: {
      fontSize: 10,
      fontWeight: '800',
      color: textTertiary,
      textTransform: 'uppercase',
      letterSpacing: 0.6,
      marginBottom: 4,
    },
    bodyStrong: { fontSize: 14, fontWeight: '700', color: textPrimary },
    bodyMuted: { fontSize: 12, color: textSecondary, marginTop: 2, lineHeight: 17 },
    hintText: { fontSize: 11, color: textSecondary, lineHeight: 16, marginBottom: 4 },
    emptyText: { fontSize: 13, color: textSecondary, lineHeight: 18 },
    lodgingHighlight: {
      borderColor: isDarkMode ? 'rgba(124,108,221,0.45)' : 'rgba(124,108,221,0.35)',
    },
    stopCard: {
      borderWidth: 1,
      borderColor: theme.ui.border,
      borderRadius: 10,
      padding: 10,
      backgroundColor: isDarkMode ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.02)',
      gap: 8,
    },
    stopCardLodging: lodgingSurface,
    stopCardMain: { gap: 8 },
    stopCardText: { gap: 2 },
    stopBadgeRow: { flexDirection: 'row' },
    stopBadge: {
      fontSize: 9,
      fontWeight: '800',
      color: '#3498db',
      textTransform: 'uppercase',
      letterSpacing: 0.4,
    },
    lodgingPillRow: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 4,
      marginBottom: 2,
    },
    lodgingPillText: {
      fontSize: 9,
      fontWeight: '800',
      color: LODGING_ACCENT_COLOR,
      textTransform: 'uppercase',
      letterSpacing: 0.4,
    },
    stopTitle: { fontSize: 14, fontWeight: '700', color: textPrimary },
    stopSubtitle: { fontSize: 12, color: textSecondary },
    pinForm: { gap: 8, marginTop: 4 },
    input: {
      borderWidth: 1,
      borderColor: theme.ui.border,
      borderRadius: 8,
      paddingHorizontal: 10,
      paddingVertical: 8,
      color: theme.input.text,
      backgroundColor: theme.input.background,
      fontSize: 13,
    },
    inputMultiline: { minHeight: 64, textAlignVertical: 'top' },
    primaryBtn: {
      backgroundColor: theme.brand.accent,
      borderRadius: 8,
      paddingVertical: 9,
      alignItems: 'center',
    },
    primaryBtnText: { color: '#fff', fontWeight: '800', fontSize: 12 },
    pinnedRow: { gap: 4 },
    removePinBtn: { alignSelf: 'flex-start', paddingVertical: 2, paddingHorizontal: 4 },
    removePinText: { fontSize: 11, fontWeight: '700', color: theme.status?.error || '#ef4444' },
    linkBtn: { flexDirection: 'row', alignItems: 'center', gap: 4, marginTop: 6 },
    linkBtnText: { fontSize: 12, fontWeight: '700', color: theme.brand.primary },
    plannerBtn: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 6,
      paddingVertical: 10,
      borderRadius: 8,
      borderWidth: 1,
      borderColor: theme.ui.border,
      marginTop: 4,
    },
    plannerBtnText: { fontSize: 12, fontWeight: '800', color: theme.brand.primary },
  });
}

export default LodgingDayPanel;
